import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, Cpu, Key, CheckCircle, XCircle, CircleNotch, Plus, Trash,
  FilePlus, Scales, Warning, Info, SealCheck, FilePdf, FileCsv, Lightning,
  ArrowLeft, ArrowRight, Gift, ShieldCheck, Sparkle, Gauge, ClockCounterClockwise, EnvelopeOpen, TextAlignLeft, Code, UsersThree, Question, CaretDown, Coins,
} from "@phosphor-icons/react";
import { LCShieldLogo } from "@/components/LCShieldLogo";
import { Seo } from "@/components/Seo";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { track, setEngineMode } from "@/lib/track";
import { api } from "@/lib/api";
import { exportPDF, exportCSV } from "@/lib/export";

// Builds the localized label set consumed by the PDF/CSV export utility.
const exportLabels = (t) => ({
  title: t("ex.title"),
  generated: t("ex.generated"),
  ready: t("ex.ready"),
  notReady: t("ex.notReady"),
  score: t("ex.score"),
  critical: t("ex.critical"),
  warnings: t("ex.warnings"),
  notes: t("ex.notes"),
  discrepancies: t("ex.discrepancies"),
  document: t("ex.document"),
  lcRequires: t("ws.lcRequires"),
  docShows: t("ws.docShows"),
  reference: t("ex.reference"),
  suggestedFix: t("ex.suggestedFix"),
  checksPassed: t("ws.checksPassed"),
  sev: { critical: t("ws.sev.critical"), warning: t("ws.sev.warning"), info: t("ws.sev.info") },
  disclaimer: t("ex.disclaimer"),
  csv: t("ex.csvHeaders"),
  redactedBadge: t("ex.redactedBadge"),
});
import { useI18n, LANGS } from "@/i18n";
import { FooterLinks, AgreeNote } from "@/components/FooterLinks";
import { PaywallModal } from "@/components/PaywallModal";
import { AmendmentModal } from "@/components/AmendmentModal";
import { HistoryDrawer } from "@/components/HistoryDrawer";
import { TeamModal } from "@/components/TeamModal";
import { ApiKeyGuideModal } from "@/components/ApiKeyGuideModal";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { InteractiveTour } from "@/components/InteractiveTour";
import { PrivacyToolbar, PrivacyShield } from "@/components/Privacy";
import { MASKERS } from "@/lib/redact";
import { HowItWorks } from "@/components/HowItWorks";
import {
  getHistory, saveToHistory, deleteHistory,
  getAmendments, saveAmendment, deleteAmendment,
  getLicenseEmail, setLicenseEmail, getInviteCode, setInviteCode,
  getOnboarded, setOnboarded, getOnboardingCompleted, setOnboardingCompleted,
  getFreeCreditUsed, setFreeCreditUsed,
} from "@/lib/history";

const MODEL = "claude-sonnet-4-6";
const USD_HKD = 7.8;
const DOC_TYPES = [
  "Commercial Invoice", "Bill of Lading / Transport Doc", "Packing List",
  "Insurance Certificate", "Certificate of Origin", "Bill of Exchange / Draft", "Other Document",
];

/* ---------- Static sample discrepancy report used only to drive the guided tour ---------- */
const TOUR_SAMPLE_RESULT = {
  readiness_score: 18,
  presentation_ready: false,
  overview:
    "Sample audit of an export textiles presentation against Documentary Credit HKLC-2026-0417. Five critical discrepancies would trigger a bank rejection; review the suggested fixes before presenting. Presentation date not provided, so the 21-day period (UCP 600 Art. 14(c)) and expiry were not evaluated.",
  counts: { critical: 5, warning: 1, info: 0 },
  discrepancies: [
    {
      severity: "critical",
      title: "Late shipment",
      document: "Bill of Lading",
      lc_requirement: "Latest shipment date 2027-01-15",
      document_value: "On board date 2027-01-20",
      ucp_reference: "LC field 44C; UCP 600 Art. 20(a)(ii)",
      suggested_fix: "Request an amendment to extend the latest shipment date, or present within a credit that permits the actual on-board date.",
    },
    {
      severity: "critical",
      title: "Credit amount exceeded",
      document: "Commercial Invoice",
      lc_requirement: "USD 50,000.00 +5% (39A) = max USD 52,500.00",
      document_value: "Invoice total USD 54,000.00",
      ucp_reference: "LC field 39A; UCP 600 Art. 18(b)",
      suggested_fix: "Reduce the invoice to the credit amount or request an amendment to increase the credit value and tolerance.",
    },
    {
      severity: "critical",
      title: "Insurance Certificate not presented",
      document: "Insurance Certificate",
      lc_requirement: "Insurance Certificate required (46A)",
      document_value: "Not presented",
      ucp_reference: "LC field 46A; UCP 600 Art. 14(a)",
      suggested_fix: "Present the Insurance Certificate required by field 46A before the presentation deadline.",
    },
    {
      severity: "critical",
      title: "Packing List not presented",
      document: "Packing List",
      lc_requirement: "Packing List required (46A)",
      document_value: "Not presented",
      ucp_reference: "LC field 46A; UCP 600 Art. 14(a)",
      suggested_fix: "Present the Packing List required by field 46A before the presentation deadline.",
    },
    {
      severity: "critical",
      title: "Invoice originals short",
      document: "Commercial Invoice",
      lc_requirement: "3 originals required",
      document_value: "2 originals presented",
      ucp_reference: "LC field 46A; UCP 600 Art. 17",
      suggested_fix: "Present the full set of signed commercial invoice originals (3) as required by field 46A.",
    },
    {
      severity: "warning",
      title: "Description wording mismatch",
      document: "Commercial Invoice",
      lc_requirement: "45A \"1000 CARTONS OF 100PCT COTTON T-SHIRTS\"",
      document_value: "\"1,000 CARTONS COTTON T-SHIRTS\" (\"100%\" omitted)",
      ucp_reference: "UCP 600 Art. 18(c)",
      suggested_fix: "Align the goods description on the invoice with the wording used in the credit.",
    },
  ],
  passed_checks: [
    "Beneficiary and applicant names consistent across documents",
    "Currency (USD) consistent with the credit",
  ],
};


/* ---------- Interactive sample scenarios (SWIFT MT700 + LC terms + presented docs) ---------- */
const SCENARIOS = {
  textiles: {
    approxScore: 18,
    tone: "critical",
    lc: `Documentary Credit No. HKLC-2026-0417
Issuing Bank: Bank of East Trade (Hong Kong)
Currency & Amount: USD 50,000.00  (tolerance +/- 5%)
Beneficiary: Pearl River Textiles Ltd, Shenzhen
Applicant: Harbour Retail Group, Hong Kong
Goods: 1,000 CARTONS OF 100% COTTON T-SHIRTS
Incoterms: CIF HONG KONG (Incoterms 2020)
Latest Shipment Date: 2027-01-15
Expiry Date: 2027-02-28 (in Hong Kong)
Issue Date: 2026-11-05
Presentation Period: within 21 days after shipment but within credit validity
Partial Shipment: NOT ALLOWED
Transhipment: NOT ALLOWED
Port of Loading: Shanghai, China
Port of Discharge: Hong Kong
Documents required:
 - Signed Commercial Invoice in 3 originals
 - Full set 3/3 clean "shipped on board" ocean Bill of Lading, made out to order and blank endorsed, marked "Freight Prepaid", notify Applicant
 - Packing List in 2 copies
 - Insurance Certificate for 110% of CIF value, in USD, covering Institute Cargo Clauses (A)`,
    mt700: `:20:HKLC-2026-0417\n:31C:261105\n:31D:270228HONG KONG\n:50:HARBOUR RETAIL GROUP, HONG KONG\n:59:PEARL RIVER TEXTILES LTD, SHENZHEN\n:32B:USD50000,00\n:39A:05/05\n:41D:ANY BANK BY NEGOTIATION\n:43P:NOT ALLOWED\n:43T:NOT ALLOWED\n:44E:SHANGHAI, CHINA\n:44F:HONG KONG\n:44C:270115\n:45A:1000 CARTONS OF 100PCT COTTON T-SHIRTS, CIF HONG KONG\n:46A:+SIGNED COMMERCIAL INVOICE IN 3 ORIGINALS\\n+FULL SET 3/3 CLEAN ON BOARD OCEAN B/L\\n+PACKING LIST IN 2 COPIES\\n+INSURANCE CERTIFICATE FOR 110PCT CIF VALUE, ICC(A)\n:47A:+DOCUMENTS TO BE PRESENTED WITHIN 21 DAYS AFTER SHIPMENT\n:48:21`,
    docs: [
      {
        id: "t1", doc_type: "Commercial Invoice",
        content: `COMMERCIAL INVOICE  No. INV-88213
Seller: Pearl River Textiles Ltd
Buyer: Harbour Retail Group, Hong Kong
Goods: 1,000 CARTONS COTTON T-SHIRTS
Unit price / total: USD 54.00 x 1,000 = USD 54,000.00
Terms: CIF Hong Kong
Presented: 2 originals`,
      },
      {
        id: "t2", doc_type: "Bill of Lading / Transport Doc",
        content: `OCEAN BILL OF LADING  No. BL-SHA-99120
Shipped on board date: 2027-01-20
Port of loading: Shanghai   Port of discharge: Hong Kong
Freight: PREPAID   Full set: 3/3 originals
Clean on board. Made out to order, blank endorsed. Notify: Harbour Retail Group`,
      },
    ],
  },

  electronics: {
    approxScore: 95,
    tone: "ok",
    lc: `Documentary Credit No. HKLC-2026-0912
Issuing Bank: Bank of East Trade (Hong Kong)
Currency & Amount: USD 480,000.00  (tolerance +/- 0%)
Beneficiary: Shenzhen Precision Machinery Co., Ltd
Applicant: Kowloon Industrial Holdings, Hong Kong
Goods: 20 UNITS CNC MACHINING CENTERS, MODEL PX-500
Incoterms: CIF HONG KONG (Incoterms 2020)
Latest Shipment Date: 2027-01-15
Expiry Date: 2027-02-28 (in Hong Kong)
Presentation Period: within 21 days after shipment but within credit validity
Partial Shipment: NOT ALLOWED
Transhipment: NOT ALLOWED
Port of Loading: Shenzhen (Yantian), China
Port of Discharge: Hong Kong
Documents required:
 - Signed Commercial Invoice in 3 originals
 - Full set 3/3 clean "shipped on board" ocean Bill of Lading, made out to order and blank endorsed, marked "Freight Prepaid", notify Applicant
 - Packing List in 2 copies
 - Insurance Certificate for 110% of CIF value, in USD, covering Institute Cargo Clauses (A)`,
    mt700: `:20:HKLC-2026-0912\n:31C:261105\n:31D:270228HONG KONG\n:50:KOWLOON INDUSTRIAL HOLDINGS, HONG KONG\n:59:SHENZHEN PRECISION MACHINERY CO LTD\n:32B:USD480000,00\n:39A:00/00\n:41D:ANY BANK BY NEGOTIATION\n:43P:NOT ALLOWED\n:43T:NOT ALLOWED\n:44E:SHENZHEN YANTIAN, CHINA\n:44F:HONG KONG\n:44C:270115\n:45A:20 UNITS CNC MACHINING CENTERS MODEL PX-500, CIF HONG KONG\n:46A:+SIGNED COMMERCIAL INVOICE IN 3 ORIGINALS\\n+FULL SET 3/3 CLEAN ON BOARD OCEAN B/L\\n+PACKING LIST IN 2 COPIES\\n+INSURANCE CERTIFICATE FOR 110PCT CIF VALUE, ICC(A)\n:47A:+DOCUMENTS TO BE PRESENTED WITHIN 21 DAYS AFTER SHIPMENT\n:48:21`,
    docs: [
      {
        id: "e1", doc_type: "Commercial Invoice",
        content: `COMMERCIAL INVOICE  No. INV-SPM-4471
Seller: Shenzhen Precision Machinery Co., Ltd
Buyer: Kowloon Industrial Holdings, Hong Kong
Goods: 20 UNITS CNC MACHINING CENTERS, MODEL PX-500
Unit price / total: USD 24,000.00 x 20 = USD 480,000.00
Terms: CIF Hong Kong (Incoterms 2020)
Signed. Presented: 3 originals`,
      },
      {
        id: "e2", doc_type: "Bill of Lading / Transport Doc",
        content: `OCEAN BILL OF LADING  No. BL-YTN-33045
Shipped on board date: 2027-01-10
Port of loading: Shenzhen (Yantian)   Port of discharge: Hong Kong
Freight: PREPAID   Full set: 3/3 originals
Clean on board. Made out to order, blank endorsed. Notify: Kowloon Industrial Holdings`,
      },
      {
        id: "e3", doc_type: "Packing List",
        content: `PACKING LIST  No. PL-SPM-4471  (2 copies)
20 UNITS CNC MACHINING CENTERS, MODEL PX-500
20 wooden crates · Gross weight: 96,000 KG · Net weight: 88,000 KG
Matches Commercial Invoice INV-SPM-4471`,
      },
      {
        id: "e4", doc_type: "Insurance Certificate",
        content: `INSURANCE CERTIFICATE  No. INS-HK-77120
Insured: 110% of CIF value = USD 528,000.00
Currency: USD   Cover: Institute Cargo Clauses (A)
Covering warehouse-to-warehouse, Shenzhen to Hong Kong. Claims payable in Hong Kong.`,
      },
    ],
  },

  commodities: {
    approxScore: 55,
    tone: "warning",
    lc: `Documentary Credit No. HKLC-2026-1130
Issuing Bank: Bank of East Trade (Hong Kong)
Currency & Amount: USD 220,000.00  (tolerance +/- 10%)
Beneficiary: Mekong Agro Exports Co., Ltd, Vietnam
Applicant: Golden Harvest Trading, Hong Kong
Goods: 500 METRIC TONS VIETNAMESE WHITE RICE, 5% BROKEN
Incoterms: CFR HONG KONG (Incoterms 2020)
Latest Shipment Date: 2027-01-15
Expiry Date: 2027-02-28 (in Hong Kong)
Issue Date: 2026-11-05
Presentation Period: within 21 days after shipment but within credit validity
Partial Shipment: ALLOWED
Transhipment: NOT ALLOWED
Port of Loading: Ho Chi Minh City, Vietnam
Port of Discharge: Hong Kong
Draft/Bill of Exchange: at sight, for 100% of invoice value, drawn on the issuing bank (Bank of East Trade, Hong Kong)
Documents required:
 - Signed Commercial Invoice in 3 originals
 - Full set 3/3 clean "shipped on board" ocean Bill of Lading
 - Packing List
 - Certificate of Origin issued by the Vietnam Chamber of Commerce and Industry
 - Bill of Exchange (draft) at sight for 100% of invoice value drawn on the issuing bank`,
    mt700: `:20:HKLC-2026-1130\n:31C:261105\n:31D:270228HONG KONG\n:50:GOLDEN HARVEST TRADING, HONG KONG\n:59:MEKONG AGRO EXPORTS CO LTD, VIETNAM\n:32B:USD220000,00\n:39A:10/10\n:41D:ANY BANK BY NEGOTIATION\n:42C:AT SIGHT\n:42D:BANK OF EAST TRADE, HONG KONG\n:43P:ALLOWED\n:43T:NOT ALLOWED\n:44E:HO CHI MINH CITY, VIETNAM\n:44F:HONG KONG\n:44C:270115\n:45A:500 METRIC TONS VIETNAMESE WHITE RICE 5PCT BROKEN, CFR HONG KONG\n:46A:+SIGNED COMMERCIAL INVOICE IN 3 ORIGINALS\\n+FULL SET 3/3 CLEAN ON BOARD OCEAN B/L\\n+PACKING LIST\\n+CERTIFICATE OF ORIGIN ISSUED BY VIETNAM CHAMBER OF COMMERCE\\n+BILL OF EXCHANGE AT SIGHT FOR 100PCT INVOICE VALUE DRAWN ON ISSUING BANK\n:47A:+DOCUMENTS TO BE PRESENTED WITHIN 21 DAYS AFTER SHIPMENT\n:48:21`,
    docs: [
      {
        id: "c1", doc_type: "Commercial Invoice",
        content: `COMMERCIAL INVOICE  No. INV-MKG-2201
Seller: Mekong Agro Exports Co., Ltd, Vietnam
Buyer: Golden Harvest Trading, Hong Kong
Goods: 500 METRIC TONS VIETNAMESE WHITE RICE, 5% BROKEN
Unit price / total: USD 440.00/MT x 500 = USD 220,000.00
Terms: CFR Hong Kong (Incoterms 2020)
Signed. Presented: 3 originals`,
      },
      {
        id: "c2", doc_type: "Bill of Lading / Transport Doc",
        content: `OCEAN BILL OF LADING  No. BL-HCM-51188
Shipped on board date: 2027-01-10
Port of loading: Ho Chi Minh City   Port of discharge: Hong Kong
Freight: COLLECT   Full set: 3/3 originals
Clean on board. Consigned to order of shipper, blank endorsed. Notify: Golden Harvest Trading`,
      },
      {
        id: "c3", doc_type: "Packing List",
        content: `PACKING LIST  No. PL-MKG-2201
500 METRIC TONS WHITE RICE, 5% BROKEN
10,000 poly bags x 50 KG each · Gross 505 MT · Net 500 MT`,
      },
      {
        id: "c4", doc_type: "Certificate of Origin",
        content: `CERTIFICATE OF ORIGIN  No. CO-TH-9931
Country of Origin: THAILAND
Issued by: Thai Chamber of Commerce
Goods: WHITE RICE, 5% BROKEN, 500 METRIC TONS
Consignee: Golden Harvest Trading, Hong Kong`,
      },
      {
        id: "c5", doc_type: "Bill of Exchange / Draft",
        content: `BILL OF EXCHANGE  No. BOE-2201
Tenor: at 30 days after sight
Amount: USD 200,000.00
Drawn on: Golden Harvest Trading, Hong Kong (Applicant)
Pay to the order of Mekong Agro Exports Co., Ltd
Drawn under Documentary Credit No. HKLC-2026-1130`,
      },
    ],
  },
};

const SCENARIO_KEYS = ["textiles", "electronics", "commodities"];

/* ---------- small presentational helpers ---------- */
const SEV = {
  critical: { color: "#DC2626", bg: "#FEF2F2", ring: "#FECACA", Icon: XCircle, label: "Critical" },
  warning: { color: "#B45309", bg: "#FFFBEB", ring: "#FDE68A", Icon: Warning, label: "Warning" },
  info: { color: "#0369A1", bg: "#F0F9FF", ring: "#BAE6FD", Icon: Info, label: "Note" },
};

function ScoreRing({ score = 0, ready }) {
  const r = 46, c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const stroke = ready ? "#10B981" : pct >= 60 ? "#F59E0B" : "#DC2626";
  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg viewBox="0 0 110 110" className="h-full w-full -rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="#E2E8F0" strokeWidth="9" />
        <motion.circle
          cx="55" cy="55" r={r} fill="none" stroke={stroke} strokeWidth="9" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - (pct / 100) * c }} transition={{ duration: 1, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-3xl font-medium text-lc-ink">{pct}</span>
        <span className="font-mono text-[9px] uppercase tracking-widest text-lc-steel">/ 100</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { t, lang, setLang } = useI18n();
  const [mode, setMode] = useState("managed");  const [byoKey, setByoKey] = useState("");
  const [keyStatus, setKeyStatus] = useState("idle"); // idle | checking | valid | invalid
  const [lcTerms, setLcTerms] = useState("");
  const [documents, setDocuments] = useState([{ id: "d1", doc_type: "Commercial Invoice", content: "" }]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [resultId, setResultId] = useState(null);
  const resultRef = useRef(null);
  useEffect(() => { resultRef.current = result; }, [result]);

  // input mode + SWIFT
  const [inputMode, setInputMode] = useState("manual"); // manual | swift
  const [swiftText, setSwiftText] = useState("");
  const [mapping, setMapping] = useState(false);

  // gating
  const [entitled, setEntitled] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFreeUsed, setPaywallFreeUsed] = useState(false); // headline variant
  const [isSampleData, setIsSampleData] = useState(false); // true when the loaded LC/docs came from a sample
  const [freeUsed, setFreeUsed] = useState(getFreeCreditUsed());
  const [redactSnapshot, setRedactSnapshot] = useState(null);
  const redactCount = redactSnapshot
    ? [lcTerms, swiftText, ...documents.map((d) => d.content)]
        .join("\n")
        .match(/\[REDACTED_(?:AMOUNT|PARTY|ACCOUNT)\]/g)?.length || 0
    : 0;
  const [autoMask, setAutoMask] = useState(() => localStorage.getItem("lc_auto_mask_enabled") === "true");
  const toggleAutoMask = (v) => {
    setAutoMask(v);
    if (v) localStorage.setItem("lc_auto_mask_enabled", "true");
    else localStorage.removeItem("lc_auto_mask_enabled");
  };

  const applyMaskAll = () => {
    setRedactSnapshot((snap) => snap || { lcTerms, swiftText, documents: documents.map((d) => ({ ...d })) });
    const all = (v) => MASKERS.accounts(MASKERS.parties(MASKERS.amounts(v)));
    setLcTerms((v) => all(v));
    setSwiftText((v) => all(v));
    setDocuments((docs) => docs.map((d) => ({ ...d, content: all(d.content) })));
    toast.success(t("priv.toast.masked"));
  };
  const handleAutoPaste = () => { if (autoMask) setTimeout(applyMaskAll, 0); };

  const applyMask = (type) => {
    const fn = MASKERS[type];
    if (!fn) return;
    setRedactSnapshot((snap) => snap || { lcTerms, swiftText, documents: documents.map((d) => ({ ...d })) });
    setLcTerms((v) => fn(v));
    setSwiftText((v) => fn(v));
    setDocuments((docs) => docs.map((d) => ({ ...d, content: fn(d.content) })));
    toast.success(t("priv.toast.masked"));
  };
  const resetRedaction = () => {
    if (!redactSnapshot) return;
    setLcTerms(redactSnapshot.lcTerms);
    setSwiftText(redactSnapshot.swiftText);
    setDocuments(redactSnapshot.documents.map((d) => ({ ...d })));
    setRedactSnapshot(null);
    toast.info(t("priv.toast.reset"));
  };
  const [quota, setQuota] = useState({ entitled: false, remaining: 1, used: 0, free_limit: 1, plan: null });
  const [teamOpen, setTeamOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [keyGuideOpen, setKeyGuideOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [keyMsg, setKeyMsg] = useState(null); // {ok:bool, text}

  // history
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // amendment
  const [amendmentOpen, setAmendmentOpen] = useState(false);
  const [amendmentLoading, setAmendmentLoading] = useState(false);
  const [amendment, setAmendment] = useState(null);
  const [savedAmendments, setSavedAmendments] = useState([]);
  const [amendmentBrowse, setAmendmentBrowse] = useState(false);

  const refreshQuota = useCallback(async (licenseOverride) => {
    const email = licenseOverride || getLicenseEmail();
    const inv = getInviteCode();
    try {
      const r = await api.get("/quota", {
        params: { license_email: email || undefined, invite_code: inv || undefined },
      });
      setQuota(r.data);
      setEntitled(!!r.data.entitled);
      return r.data;
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => {
    setHistory(getHistory());
    setSavedAmendments(getAmendments());
    setEngineMode(mode);
    const params = new URLSearchParams(window.location.search);
    const inv = params.get("invite");
    if (inv) { setInviteCode(inv); toast.success(t("ws.toast.invite")); }

    const demo = params.get("demo");
    if (demo === "mt700") {
      setInputMode("swift");
      setSwiftText(SAMPLE_MT700);
      toast.info(t("ws.toast.demoScan"));
    }

    // ---- Single Audit Pass return: verify payment + grant 1 audit credit ----
    const singleSession = params.get("single_session_id");
    if (singleSession) {
      (async () => {
        try {
          const r = await api.post("/payments/redeem-single", { session_id: singleSession });
          if (r.data?.paid && r.data?.is_single) {
            setFreeCreditUsed(false);
            setFreeUsed(false);
            await refreshQuota();
            toast.success(t("ws.toast.singleCredit"));
          }
        } catch { /* ignore */ }
        const url = new URL(window.location.href);
        url.searchParams.delete("single_session_id");
        window.history.replaceState({}, "", url.pathname + url.search);
      })();
    }

    // ---- Stripe return: auto-fill email + auto-unlock ----
    const sessionId = params.get("session_id");
    const emailParam = params.get("email");
    const celebrate = () => toast.success(t("ws.toast.paid"));
    (async () => {
      if (emailParam) {
        const r = await refreshQuota(emailParam.toLowerCase());
        if (r?.entitled) { setLicenseEmail(emailParam.toLowerCase()); celebrate(); }
        else { refreshQuota(); }
      } else if (sessionId) {
        try {
          const st = await api.get(`/payments/status/${sessionId}`);
          if (st.data.payment_status === "paid" && st.data.customer_email) {
            setLicenseEmail(st.data.customer_email.toLowerCase());
            await refreshQuota(st.data.customer_email.toLowerCase());
            celebrate();
          } else { refreshQuota(); }
        } catch { refreshQuota(); }
      } else {
        refreshQuota();
      }
    })();

    // ---- first-time interactive guided walkthrough (auto-launch once) ----
    // Auto-launch for fresh visitors, OR on demand via ?tour=1 (1-Click Live Sample CTA).
    const wantTour = params.get("tour") === "1";
    if (wantTour || (!getOnboardingCompleted() && demo !== "mt700")) {
      setTimeout(() => setTourOpen(true), 900); // let the workspace paint first
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshQuota]);

  const byoActive = mode === "byo" && byoKey.trim().length > 0;
  // Managed run allowed purely on the server-computed remaining allowance (metered plan
  // month balance, or the free IP trial for Solo/free). BYO is gated separately below.
  const canRunManaged = (quota.remaining ?? 0) > 0;
  const metered = quota.monthly_limit != null;

  /* ---------- token cost estimator ---------- */
  const estimate = useMemo(() => {
    const inputChars = lcTerms.length + documents.reduce((s, d) => s + d.content.length, 0) + 1800;
    const inTok = Math.round(inputChars / 4);
    const outTok = 1300;
    const usd = (inTok / 1e6) * 3 + (outTok / 1e6) * 15;
    return { inTok, outTok, usd, hkd: usd * USD_HKD };
  }, [lcTerms, documents]);

  // Returns true if the managed engine may run; otherwise opens the paywall. BYO always allowed.
  const gateManaged = () => {
    if (mode === "byo") {
      if (!byoKey.trim()) { toast.error(t("ws.toast.needKey")); return false; }
      return true;
    }
    if (!canRunManaged) { track("paywall_modal_opened", { plan: "workspace" }); setPaywallOpen(true); return false; }
    return true;
  };
  // The server consumes quota on each managed request; re-sync and return the fresh state.
  const afterManagedRun = async () => { if (mode === "managed") return await refreshQuota(); };

  // Header low-credit nudge → open the Single Audit Pass Stripe checkout directly.
  const buySinglePass = async () => {
    try {
      const ccy = localStorage.getItem("lc_ccy") || "usd";
      const lookup_key = ccy === "hkd" ? "lc_single_audit" : "lc_single_audit_usd";
      track("checkout_initiated", { plan: "single", source: "header_nudge" });
      const r = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch {
      toast.error(t("ws.toast.checkoutError") || "Could not start checkout. Please try again.");
    }
  };
  const handleEngineError = (e, fallback) => {
    if (e?.response?.status === 402) { track("paywall_modal_opened", { plan: "workspace" }); setPaywallOpen(true); refreshQuota(); toast.error(e.response.data.detail); }
    else { toast.error(e?.response?.data?.detail || fallback); }
  };
  const enginePayload = () => ({
    use_managed: mode === "managed",
    byo_key: mode === "byo" ? byoKey.trim() : null,
    model: MODEL,
    license_email: getLicenseEmail() || null,
    invite_code: getInviteCode() || null,
  });

  /* ---------- key connection test ---------- */
  const validateKey = async () => {
    if (!byoKey.trim()) { toast.error(t("ws.toast.pasteKey")); return; }
    setKeyStatus("checking");
    setKeyMsg(null);
    try {
      const r = await api.post("/validate-key", { api_key: byoKey.trim() });
      if (r.data.valid) {
        setKeyStatus("valid");
        setKeyMsg({ ok: true, text: t("ws.toast.connOk") });
        toast.success(t("ws.toast.connOk"));
      } else {
        setKeyStatus("invalid");
        setKeyMsg({ ok: false, text: t("ws.toast.connBad") });
        toast.error(t("ws.toast.connBad"));
      }
    } catch {
      setKeyStatus("invalid");
      setKeyMsg({ ok: false, text: t("ws.toast.connBad") });
      toast.error(t("ws.toast.connBad"));
    }
  };

  /* ---------- documents ---------- */
  const addDoc = () =>
    setDocuments((d) => [...d, { id: `d${Date.now()}`, doc_type: "Other Document", content: "" }]);
  const updateDoc = (id, patch) =>
    setDocuments((d) => d.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeDoc = (id) => setDocuments((d) => (d.length > 1 ? d.filter((x) => x.id !== id) : d));

  const uploadToDoc = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateDoc(id, { content: r.data.text });
      setIsSampleData(false);
      toast.success(t("ws.toast.loadedChars").replace("{n}", r.data.chars.toLocaleString()).replace("{file}", file.name));
    } catch {
      toast.error(t("ws.toast.readFail"));
    }
  };

  const loadScenario = (key) => {
    const s = SCENARIOS[key] || SCENARIOS.textiles;
    setLcTerms(s.lc);
    setSwiftText(s.mt700);
    setDocuments(s.docs.map((d) => ({ ...d })));
    setInputMode("manual");
    setResult(null);
    setIsSampleData(true);
    track("demo_scenario_loaded", { scenario: key });
    toast.info(t(`ws.scn.${key}`) + " · " + t("ws.toast.sampleLoaded"));
  };

  const clearWorkspace = () => {
    setLcTerms("");
    setSwiftText("");
    setDocuments([{ id: "d1", doc_type: "Commercial Invoice", content: "" }]);
    setResult(null);
    setResultId(null);
    setInputMode("manual");
    setIsSampleData(false);
    toast.info(t("ws.toast.cleared"));
  };

  // During the tour, guarantee Step 4/5 highlight real populated cards.
  const applyTourSample = () => {
    if (resultRef.current) return; // don't overwrite the user's real work
    const s = SCENARIOS.textiles;
    setLcTerms(s.lc);
    setSwiftText(s.mt700);
    setDocuments(s.docs.map((d) => ({ ...d })));
    setInputMode("manual");
    setResult(TOUR_SAMPLE_RESULT);
    setResultId("tour-sample");
    setIsSampleData(true);
  };

  const handleTourStep = (index) => {
    if (index >= 4) applyTourSample(); // Step 5 (Discrepancy Results) onward
  };

  /* ---------- SWIFT MT700 mapper ---------- */
  const SAMPLE_MT700 = SCENARIOS.textiles.mt700;

  const mapMt700 = async () => {
    if (!swiftText.trim()) { toast.error(t("ws.toast.needSwift")); return; }
    if (!gateManaged()) return;
    setMapping(true);
    try {
      const r = await api.post("/mt700-map", { mt700_text: swiftText, ...enginePayload() });
      const f = r.data.fields;
      if (f?.lc_terms_text) setLcTerms(f.lc_terms_text);
      afterManagedRun();
      setInputMode("manual");
      toast.success(t("ws.toast.mapped"));
    } catch (e) {
      handleEngineError(e, t("ws.errMap"));
    } finally {
      setMapping(false);
    }
  };

  /* ---------- amendment drafter ---------- */
  const draftAmendment = async () => {
    if (!gateManaged()) return;
    setAmendment(null);
    setAmendmentBrowse(false);
    setAmendmentLoading(true);
    setAmendmentOpen(true);
    try {
      const critical = (result?.discrepancies || []).filter((d) => d.severity === "critical");
      const r = await api.post("/amendment", {
        lc_terms: lcTerms,
        discrepancies: critical.length ? critical : (result?.discrepancies || []),
        lang,
        ...enginePayload(),
      });
      // Save first so the in-memory draft shares the saved item's id (no duplicate/back-link).
      const savedItem = saveAmendment({ ...r.data.amendment, lang });
      setAmendment(savedItem);
      setSavedAmendments(getAmendments());
      afterManagedRun();
    } catch (e) {
      handleEngineError(e, t("ws.errAmendment"));
      setAmendmentOpen(false);
    } finally {
      setAmendmentLoading(false);
    }
  };

  /* ---------- history ---------- */
  const loadFromHistory = (it) => {
    setLcTerms(it.lc_terms || "");
    setDocuments((it.documents || []).map((d, i) => ({ id: `h${i}`, doc_type: d.doc_type, content: d.content })));
    setResult(it.result);
    setResultId(it.id);
    setHistoryOpen(false);
    setInputMode("manual");
    toast.info(t("ws.toast.histLoaded"));
  };
  const removeFromHistory = (id) => setHistory(deleteHistory(id));

  /* ---------- analyze ---------- */
  const analyze = useCallback(async () => {
    if (!lcTerms.trim()) { toast.error(t("ws.toast.needLc")); return; }
    if (!documents.some((d) => d.content.trim())) { toast.error(t("ws.toast.needDoc")); return; }

    // 1 free REAL audit (own data). Sample scenarios are always free and never consume it.
    const realAudit = !isSampleData;
    const managedFree = mode === "managed" && !entitled;
    if (realAudit && managedFree && getFreeCreditUsed()) {
      track("paywall_modal_opened", { plan: "workspace", reason: "free_credit_used" });
      setPaywallFreeUsed(true);
      setPaywallOpen(true);
      return;
    }
    // Samples run free server-side (is_sample) — bypass the managed quota gate so the
    // 1-Click Live Sample always renders results even after the free real audit is used.
    if (isSampleData) {
      if (mode === "byo" && !byoKey.trim()) { toast.error(t("ws.toast.needKey")); return; }
    } else if (!gateManaged()) {
      return;
    }

    setAnalyzing(true);
    setResult(null);
    try {
      const cleanDocs = documents.filter((d) => d.content.trim()).map((d) => ({ doc_type: d.doc_type, content: d.content }));
      const r = await api.post("/analyze", { lc_terms: lcTerms, documents: cleanDocs, lang, is_sample: isSampleData, ...enginePayload() });
      track("discrepancy_check_run", { mode: mode === "byo" ? "byo" : "managed", sample: isSampleData });
      setResult({ ...r.data.result, _redacted: !!redactSnapshot });
      setResultId(r.data.id);
      const q = await afterManagedRun();
      if (realAudit && managedFree) {
        setFreeCreditUsed(true); setFreeUsed(true);
        // Free audit spent + no purchased credits left → surface the conversion paywall
        // right after the user sees their result (short delay, non-jarring).
        if (!q?.entitled && (q?.remaining ?? 0) <= 0) {
          setTimeout(() => {
            setPaywallFreeUsed(true);
            setPaywallOpen(true);
            track("paywall_modal_opened", { plan: "workspace", reason: "free_audit_complete" });
          }, 1400);
        }
      }
      const saved = saveToHistory({ id: r.data.id, lc_terms: lcTerms, documents: cleanDocs, result: r.data.result });
      setHistory(getHistory());
      toast.success(t("ws.toast.analysisDone"));
    } catch (e) {
      handleEngineError(e, t("ws.errAnalyze"));
    } finally {
      setAnalyzing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lcTerms, documents, mode, byoKey, entitled, quota, lang, isSampleData]);

  // Keep a live ref to analyze so the one-shot demo runner always calls the latest version.
  const analyzeRef = useRef(analyze);
  useEffect(() => { analyzeRef.current = analyze; });

  // Auto-run demo on ?demo=mt700: show the SWIFT, "map" it to LC terms + sample docs, then scan.
  // StrictMode-safe: guard prevents a second run and we intentionally don't clear the one-shot timer.
  const demoRan = useRef(false);
  useEffect(() => {
    if (demoRan.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") !== "mt700") return;
    demoRan.current = true;
    setMapping(true);
    setTimeout(() => {
      setLcTerms(SCENARIOS.textiles.lc);
      setSwiftText(SCENARIOS.textiles.mt700);
      setDocuments(SCENARIOS.textiles.docs.map((d) => ({ ...d })));
      setInputMode("manual");
      setIsSampleData(true);
      setMapping(false);
      // Show the deterministic sample result (42 / 100, Not ready) so the tour/demo
      // result card stays consistent with the home sandbox and the sample menu for this
      // LC — no live-score drift. The sample is free and consumes no audit credit.
      setTimeout(() => {
        if (resultRef.current) return;
        setResult(TOUR_SAMPLE_RESULT);
        setResultId("tour-sample");
        setIsSampleData(true);
      }, 700);
    }, 1400);
  }, []);

  const privacyActive = byoActive || entitled;

  const LangSwitcher = () => (
    <div className="flex items-center rounded-sm border border-white/10 bg-lc-slate/60 p-0.5" data-testid="ws-lang-switcher">
      {LANGS.map((l) => (
        <button key={l.code} data-testid={`ws-lang-${l.code}`} onClick={() => setLang(l.code)}
          className={`rounded-sm px-1.5 py-1 text-[10px] font-semibold transition-colors duration-200 ${
            lang === l.code ? "bg-lc-sky text-lc-navy" : "text-lc-fog hover:text-white"
          }`}>{l.label}</button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-lc-paper font-sans text-lc-ink flex flex-col overflow-x-hidden">
      <Seo
        path="/workspace"
        title="Workspace | LC Shield — Run a Letter of Credit Discrepancy Check"
        description="Paste your Letter of Credit and trade documents to scan for UCP 600 discrepancies, map SWIFT MT700 messages, and draft amendment letters — all privacy-first, in your browser."
      />
      {/* ---------- TOP BAR ---------- */}
      <header className="sticky top-0 z-40 border-b border-lc-ink/10 bg-lc-navy text-white">
        <div className="mx-auto max-w-[1500px] px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => nav("/")} data-testid="back-home-btn"
              className="text-lc-fog hover:text-white transition-colors duration-200" aria-label="Back to site">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <div className="flex items-center gap-2">
              <LCShieldLogo className="text-lc-sky" size={22} />
              <span className="font-serif text-lg whitespace-nowrap">LC Shield</span>
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-lc-fog">{t("ws.workspace")}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* audit balance badge — metered plans show monthly usage; free/solo show credits */}
            {mode === "managed" && (() => {
              const remaining = quota.remaining ?? 0;
              if (metered) {
                if (remaining <= 0) {
                  return (
                    <button
                      data-testid="credit-nudge-buy"
                      onClick={() => { setPaywallFreeUsed(true); setPaywallOpen(true); track("paywall_modal_opened", { plan: quota.plan || "workspace", reason: "monthly_limit" }); }}
                      title={t("ws.credits.limitTitle")}
                      className="flex items-center gap-1.5 rounded-sm border border-lc-warn/50 bg-lc-warn/10 px-3 py-1.5 hover:bg-lc-warn/20 transition-colors duration-200"
                    >
                      <Coins size={14} weight="duotone" className="text-lc-warn" />
                      <span className="font-mono text-[10px] uppercase tracking-wider text-lc-warn whitespace-nowrap">
                        {t("ws.credits.limitReached").replace("{used}", String(quota.used_month ?? quota.monthly_limit)).replace("{limit}", String(quota.monthly_limit))}
                      </span>
                    </button>
                  );
                }
                return (
                  <div data-testid="credit-balance-badge" title={t("ws.credits.monthlyTitle")}
                    className="flex items-center gap-1.5 rounded-sm border border-lc-sky/30 bg-lc-sky/10 px-3 py-1.5">
                    <Coins size={14} weight="duotone" className="text-lc-sky" />
                    <span className="font-mono text-[10px] uppercase tracking-wider text-lc-sky whitespace-nowrap">
                      {t("ws.credits.monthly").replace("{used}", String(quota.used_month ?? 0)).replace("{limit}", String(quota.monthly_limit))}
                    </span>
                  </div>
                );
              }
              return remaining <= 0 ? (
                <button
                  data-testid="credit-nudge-buy"
                  onClick={buySinglePass}
                  title={t("ws.credits.nudgeTitle")}
                  className="flex items-center gap-1.5 rounded-sm border border-lc-warn/50 bg-lc-warn/10 px-3 py-1.5 hover:bg-lc-warn/20 transition-colors duration-200"
                >
                  <Coins size={14} weight="duotone" className="text-lc-warn" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-lc-warn whitespace-nowrap">
                    {t("ws.credits.nudge")}
                  </span>
                </button>
              ) : (
                <div data-testid="credit-balance-badge" title={t("ws.credits.title")}
                  className="flex items-center gap-1.5 rounded-sm border border-lc-sky/30 bg-lc-sky/10 px-3 py-1.5">
                  <Coins size={14} weight="duotone" className="text-lc-sky" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-lc-sky whitespace-nowrap">
                    {t("ws.credits.left").replace("{n}", String(remaining))}
                  </span>
                </div>
              );
            })()}
            {/* mobile overflow menu (below sm): History / Team / Guided Tour */}
            <details data-testid="ws-mobile-menu" className="relative sm:hidden">
              <summary className="list-none flex items-center justify-center rounded-sm border border-white/10 bg-lc-slate/60 text-lc-fog cursor-pointer" style={{ minWidth: 44, minHeight: 44 }} aria-label="More">
                <span className="font-bold text-lg leading-none">⋯</span>
              </summary>
              <div className="absolute right-0 mt-1 z-50 w-44 rounded-sm border border-white/10 bg-lc-navy p-1 shadow-xl">
                <button data-testid="m-history" onClick={() => { setHistory(getHistory()); setHistoryOpen(true); }} className="w-full text-left px-3 py-2 text-xs text-lc-fog hover:bg-white/5 rounded-sm">{t("ws.history")}</button>
                <button data-testid="m-team" onClick={() => setTeamOpen(true)} className="w-full text-left px-3 py-2 text-xs text-lc-fog hover:bg-white/5 rounded-sm">{t("ws.team")}</button>
                <button data-testid="m-tour" onClick={() => setTourOpen(true)} className="w-full text-left px-3 py-2 text-xs text-lc-fog hover:bg-white/5 rounded-sm">{t("ws.quickTour")}</button>
              </div>
            </details>
            {/* history button */}
            <button data-testid="history-btn" onClick={() => { setHistory(getHistory()); setHistoryOpen(true); }}
              className="hidden sm:flex items-center gap-1.5 rounded-sm border border-white/10 bg-lc-slate/60 px-3 py-1.5 text-lc-fog hover:text-white transition-colors duration-200">
              <ClockCounterClockwise size={14} weight="duotone" className="text-lc-sky" />
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-wider">{t("ws.history")}</span>
            </button>
            {/* saved amendment letters button */}
            {savedAmendments.length > 0 && (
              <button data-testid="letters-btn" onClick={() => { setSavedAmendments(getAmendments()); setAmendmentBrowse(true); setAmendmentOpen(true); }}
                className="relative hidden sm:flex items-center gap-1.5 rounded-sm border border-white/10 bg-lc-slate/60 px-3 py-1.5 text-lc-fog hover:text-white transition-colors duration-200">
                <EnvelopeOpen size={14} weight="duotone" className="text-lc-sky" />
                <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-wider">{t("ws.letters")}</span>
                <span data-testid="letters-count" className="inline-flex min-w-[16px] items-center justify-center rounded-full bg-lc-sky px-1.5 text-[10px] font-bold leading-none text-lc-navy" style={{ height: 16 }}>{savedAmendments.length}</span>
              </button>
            )}
            {/* team button */}
            <button data-testid="team-btn" onClick={() => setTeamOpen(true)}
              className="hidden sm:flex items-center gap-1.5 rounded-sm border border-white/10 bg-lc-slate/60 px-3 py-1.5 text-lc-fog hover:text-white transition-colors duration-200">
              <UsersThree size={14} weight="duotone" className="text-lc-sky" />
              <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-wider">{t("ws.team")}</span>
            </button>
            {/* guided tour button */}
            <button data-testid="quick-tour-btn" onClick={() => setTourOpen(true)}
              className="hidden sm:flex items-center gap-1.5 rounded-sm border border-lc-warn/30 bg-lc-warn/10 px-3 py-1.5 text-lc-warn hover:bg-lc-warn/20 transition-colors duration-200">
              <Sparkle size={14} weight="fill" />
              <span className="hidden md:inline font-mono text-[10px] uppercase tracking-wider">{t("ws.quickTour")}</span>
            </button>
            {/* how it works button */}
            <button data-testid="howitworks-btn" onClick={() => setHowOpen(true)}
              className="hidden sm:flex items-center gap-1.5 rounded-sm border border-lc-sky/30 bg-lc-sky/10 px-3 py-1.5 text-lc-sky hover:bg-lc-sky/20 transition-colors duration-200">
              <Question size={14} weight="fill" />
              <span className="hidden md:inline font-mono text-[10px] uppercase tracking-wider">{t("ws.howItWorks")}</span>
            </button>
            {/* privacy badge */}
            <div data-testid="privacy-badge" title={t("privacy.badge")}
              className="hidden lg:flex items-center gap-2 rounded-sm border border-white/10 bg-lc-slate/60 px-3 py-1.5">
              <span className={`h-2 w-2 rounded-full ${privacyActive ? "bg-lc-ok pulse-dot" : "bg-lc-ok/70 pulse-dot"}`} />
              <Lock size={13} weight="duotone" className="text-lc-ok" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-lc-fog">
                {t("ws.zeroStorage")}
              </span>
            </div>
            {/* key status */}
            <div data-testid="api-key-status"
              className="hidden sm:flex items-center gap-2 rounded-sm border border-white/10 bg-lc-slate/60 px-3 py-1.5">
              <Cpu size={13} weight="duotone" className="text-lc-sky" />
              <span className="font-mono text-[10px] uppercase tracking-wider text-lc-fog">
                {mode === "byo"
                  ? (keyStatus === "valid" ? t("ws.keyVerified") : t("ws.keyNotSet"))
                  : (entitled ? t("ws.unlocked") : t("ws.demoEngine"))}
              </span>
            </div>
            {/* token cost estimator (BYO-key only) */}
            {mode === "byo" && (
            <div data-testid="cost-estimator"
              className="hidden sm:flex items-center gap-2 rounded-sm border border-lc-sky/30 bg-lc-sky/10 px-3 py-1.5">
              <Lightning size={13} weight="fill" className="text-lc-sky" />
              <span className="font-mono text-[11px] text-lc-sky">
                ~HK${estimate.hkd.toFixed(3)} <span className="text-lc-fog">{t("ws.perRun")}</span>
              </span>
            </div>
            )}
          </div>
        </div>
      </header>

      {/* ---------- MAIN GRID ---------- */}
      <main className="mx-auto w-full max-w-[1500px] flex-1 px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT: inputs */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          {/* free real-audit indicator */}
          {!entitled && mode === "managed" && (
            freeUsed ? (
              <div data-testid="free-audit-banner-used"
                className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-lc-warn/30 bg-lc-warn/10 px-4 py-2.5">
                <span className="flex items-center gap-2 text-xs font-semibold text-lc-ink">
                  <Warning size={15} weight="fill" className="text-lc-warn" /> {t("ws.freeBanner.used")}
                </span>
                <button data-testid="free-audit-upgrade-btn"
                  onClick={() => { setPaywallFreeUsed(true); setPaywallOpen(true); track("paywall_modal_opened", { plan: "workspace", reason: "free_banner" }); }}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-lc-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
                  {t("ws.freeBanner.upgrade")} <ArrowRight size={13} weight="bold" />
                </button>
              </div>
            ) : (
              <div data-testid="free-audit-banner-remaining"
                className="flex items-center gap-2 rounded-sm border border-lc-ocean/25 bg-lc-ocean/5 px-4 py-2.5 text-xs font-semibold text-lc-ocean">
                <Gift size={15} weight="duotone" /> {t("ws.freeBanner.remaining")}
              </div>
            )
          )}
          {/* engine config */}
          <section className="rounded-sm border border-lc-ink/10 bg-white p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-lg flex items-center gap-2"><Key size={18} weight="duotone" className="text-lc-ocean" /> {t("ws.engine")}</h2>
              <div className="flex rounded-sm border border-lc-ink/10 p-0.5" data-testid="engine-toggle">
                {[
                  { k: "managed", label: t("ws.demo") },
                  { k: "byo", label: t("ws.byo") },
                ].map((m) => (
                  <button key={m.k} data-testid={`engine-mode-${m.k}`} onClick={() => { setMode(m.k); setEngineMode(m.k); }}
                    className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                      mode === m.k ? "bg-lc-navy text-white" : "text-lc-steel hover:text-lc-ink"
                    }`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <AnimatePresence mode="wait">
              {mode === "byo" ? (
                <motion.div key="byo" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <input
                      data-testid="byo-key-input" type="password" value={byoKey}
                      onChange={(e) => { setByoKey(e.target.value); setKeyStatus("idle"); setKeyMsg(null); }}
                      placeholder={t("ws.phKey")}
                      className="flex-1 rounded-sm border border-lc-ink/15 bg-lc-paper px-3 py-2 font-mono text-xs outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200"
                    />
                    <button data-testid="test-connection-btn" onClick={validateKey} disabled={keyStatus === "checking"}
                      className={`inline-flex items-center justify-center gap-2 rounded-sm px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 disabled:opacity-60 ${
                        keyStatus === "valid" ? "bg-lc-ok hover:bg-lc-ok/90"
                          : keyStatus === "invalid" ? "bg-lc-critical hover:bg-lc-critical/90"
                          : "bg-lc-ok hover:bg-lc-ok/90"
                      }`}>
                      {keyStatus === "checking" ? <CircleNotch size={14} className="animate-spin" /> :
                        keyStatus === "valid" ? <CheckCircle size={14} weight="fill" /> :
                        keyStatus === "invalid" ? <XCircle size={14} weight="fill" /> :
                        <span className="h-2.5 w-2.5 rounded-full bg-white" />}
                      {keyStatus === "checking" ? t("ws.testing") : t("ws.testConnection")}
                    </button>
                  </div>
                  {keyMsg && (
                    <p data-testid="connection-status" className="mt-2 text-xs font-semibold" style={{ color: keyMsg.ok ? "#10B981" : "#DC2626" }}>
                      {keyMsg.text}
                    </p>
                  )}
                  <p className="mt-2 text-[11px] text-lc-steel">{t("ws.byoNote")} <span className="font-mono">{MODEL}</span></p>
                  <button data-testid="apikey-guide-trigger" onClick={() => setKeyGuideOpen(true)}
                    className="mt-2 text-[11px] font-semibold text-lc-ocean hover:underline transition-colors duration-200">
                    {t("apikey.trigger")}
                  </button>
                </motion.div>
              ) : (
                <motion.p key="managed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 text-[13px] text-lc-steel">
                  {metered
                    ? t("ws.managedMetered").replace("{model}", MODEL).replace("{n}", String(quota.remaining ?? 0)).replace("{limit}", String(quota.monthly_limit))
                    : quota.plan === "solo"
                      ? t("ws.managedSolo")
                      : t("ws.managedRunning").replace("{model}", MODEL).replace("{free}", (quota.remaining ?? 0) < 1 ? t("ws.freeUsed") : t("ws.freeIncluded").replace("{n}", quota.remaining))}
                </motion.p>
              )}
            </AnimatePresence>
          </section>

          {/* LC terms */}
          <section className="rounded-sm border border-lc-ink/10 bg-white p-5">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="font-serif text-lg flex items-center gap-2 whitespace-nowrap"><Scales size={18} weight="duotone" className="text-lc-ocean" /> {t("ws.lcTerms")}</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex rounded-sm border border-lc-ink/10 p-0.5" data-testid="input-mode-toggle">
                  {[
                    { k: "manual", label: t("ws.pasteManual"), Icon: TextAlignLeft },
                    { k: "swift", label: t("ws.pasteSwift"), Icon: Code },
                  ].map((m) => (
                    <button key={m.k} data-testid={`input-mode-${m.k}`} onClick={() => setInputMode(m.k)}
                      className={`inline-flex items-center gap-1 rounded-sm px-2.5 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                        inputMode === m.k ? "bg-lc-navy text-white" : "text-lc-steel hover:text-lc-ink"
                      }`}>
                      <m.Icon size={13} weight="bold" /> {m.label}
                    </button>
                  ))}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="load-sample-btn"
                      className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/10 px-2.5 py-1.5 text-xs font-semibold text-lc-steel hover:bg-lc-paper transition-colors duration-200">
                      <Sparkle size={13} weight="fill" className="text-lc-warn" /> {t("ws.loadScenario")}
                      <CaretDown size={11} weight="bold" className="opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[290px] bg-white border border-lc-ink/10 text-lc-ink" data-testid="scenario-menu">
                    {SCENARIO_KEYS.map((k) => {
                      const s = SCENARIOS[k];
                      const dot = s.tone === "ok" ? "bg-lc-ok" : s.tone === "warning" ? "bg-lc-warn" : "bg-lc-critical";
                      const scoreColor = s.tone === "ok" ? "text-lc-ok" : s.tone === "warning" ? "text-lc-warn" : "text-lc-critical";
                      return (
                        <DropdownMenuItem key={k} data-testid={`load-scenario-${k}`} onClick={() => loadScenario(k)}
                          className="flex flex-col items-start gap-0.5 cursor-pointer py-2.5 focus:bg-lc-paper">
                          <div className="flex w-full items-center justify-between gap-3">
                            <span className="font-semibold text-[13px] text-lc-ink">{t(`ws.scn.${k}`)}</span>
                            <span className={`inline-flex items-center gap-1 font-mono text-[10px] font-semibold ${scoreColor}`}>
                              <span className={`h-1.5 w-1.5 rounded-full ${dot}`} /> ~{s.approxScore}
                            </span>
                          </div>
                          <span className="text-[11px] leading-snug text-lc-steel">{t(`ws.scn.${k}Sub`)}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button data-testid="clear-workspace-btn" onClick={clearWorkspace}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-lc-critical/30 bg-lc-critical/5 px-2.5 py-1.5 text-xs font-semibold text-lc-critical hover:bg-lc-critical/10 transition-colors duration-200">
                  <Trash size={13} weight="bold" /> {t("ws.clearAll")}
                </button>
              </div>
            </div>

            <div className="mt-3">
              <PrivacyToolbar onMask={applyMask} onMaskAll={applyMaskAll} onReset={resetRedaction}
                canReset={!!redactSnapshot} autoMask={autoMask} onToggleAuto={toggleAutoMask} redactCount={redactCount} />
            </div>

            {inputMode === "swift" ? (
              <div>
                <textarea
                  data-testid="swift-input" value={swiftText} onPaste={handleAutoPaste} onChange={(e) => { setSwiftText(e.target.value); setIsSampleData(false); }} rows={8}
                  placeholder={t("ws.phSwift")}
                  className="lc-scroll mt-3 w-full resize-y rounded-sm border border-lc-ink/15 bg-lc-paper px-3 py-2.5 font-mono text-xs leading-relaxed outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button data-testid="map-mt700-btn" onClick={mapMt700} disabled={mapping}
                    className="inline-flex items-center gap-2 rounded-sm bg-lc-ocean px-4 py-2 text-xs font-semibold text-white hover:bg-lc-navy transition-colors duration-200 disabled:opacity-60">
                    {mapping ? <CircleNotch size={14} className="animate-spin" /> : <Code size={14} weight="bold" />}
                    {mapping ? t("ws.mapping") : t("ws.mapSwift")}
                  </button>
                  <button data-testid="load-swift-sample-btn" onClick={() => setSwiftText(SAMPLE_MT700)}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/10 px-2.5 py-2 text-xs font-semibold text-lc-steel hover:bg-lc-paper transition-colors duration-200">
                    <Sparkle size={13} weight="fill" className="text-lc-warn" /> {t("ws.sampleMt700")}
                  </button>
                </div>
              </div>
            ) : (
              <textarea
                data-testid="lc-terms-input" value={lcTerms} onPaste={handleAutoPaste} onChange={(e) => { setLcTerms(e.target.value); setIsSampleData(false); }} rows={8}
                placeholder={t("ws.phManual")}
                className="lc-scroll mt-3 w-full resize-y rounded-sm border border-lc-ink/15 bg-lc-paper px-3 py-2.5 font-mono text-xs leading-relaxed outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200"
              />
            )}
          </section>

          {/* documents */}
          <section className="rounded-sm border border-lc-ink/10 bg-white p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-lg flex items-center gap-2"><FilePlus size={18} weight="duotone" className="text-lc-ocean" /> {t("ws.documents")}</h2>
              <button data-testid="add-document-btn" onClick={addDoc}
                className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/10 px-2.5 py-1.5 text-xs font-semibold text-lc-steel hover:bg-lc-paper transition-colors duration-200">
                <Plus size={13} weight="bold" /> {t("ws.addDoc")}
              </button>
            </div>
            <div className="mt-3 flex flex-col gap-3">
              {documents.map((d, i) => (
                <div key={d.id} data-testid={`document-card-${i}`} className="rounded-sm border border-lc-ink/10 bg-lc-paper p-3">
                  <div className="flex items-center gap-2">
                    <select value={d.doc_type} onChange={(e) => updateDoc(d.id, { doc_type: e.target.value })}
                      data-testid={`document-type-${i}`}
                      className="flex-1 rounded-sm border border-lc-ink/15 bg-white px-2 py-1.5 text-xs font-semibold outline-none focus:border-lc-ocean transition-colors duration-200">
                      {DOC_TYPES.map((dt) => <option key={dt} value={dt}>{(t("ws.docTypeMap") || {})[dt] || dt}</option>)}
                    </select>
                    <label className="cursor-pointer rounded-sm border border-lc-ink/15 bg-white px-2.5 py-1.5 text-xs font-semibold text-lc-steel hover:bg-lc-paper transition-colors duration-200">
                      {t("ws.upload")}
                      <input type="file" accept=".pdf,.txt,.csv,.md" className="hidden"
                        data-testid={`document-upload-${i}`}
                        onChange={(e) => uploadToDoc(d.id, e.target.files?.[0])} />
                    </label>
                    <button onClick={() => removeDoc(d.id)} data-testid={`document-remove-${i}`}
                      className="rounded-sm border border-lc-ink/15 bg-white px-2 py-1.5 text-lc-steel hover:text-lc-critical hover:border-lc-critical/40 transition-colors duration-200" aria-label="Remove document">
                      <Trash size={14} />
                    </button>
                  </div>
                  <textarea value={d.content} onPaste={handleAutoPaste} onChange={(e) => { updateDoc(d.id, { content: e.target.value }); setIsSampleData(false); }} rows={4}
                    data-testid={`document-content-${i}`}
                    placeholder={t("ws.phDoc")}
                    className="lc-scroll mt-2 w-full resize-y rounded-sm border border-lc-ink/15 bg-white px-3 py-2 font-mono text-[11px] leading-relaxed outline-none focus:border-lc-ocean focus:ring-2 focus:ring-lc-ocean/20 transition-colors duration-200" />
                </div>
              ))}
            </div>

            <button data-testid="analyze-btn" onClick={analyze} disabled={analyzing}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-sm bg-lc-navy px-5 py-3.5 text-sm font-semibold text-white hover:bg-lc-ocean transition-colors duration-200 disabled:opacity-60">
              {analyzing ? <CircleNotch size={17} className="animate-spin" /> : <Scales size={17} weight="duotone" />}
              {analyzing ? t("ws.running") : t("ws.run")}
            </button>
            <AgreeNote prefix={t("ws.wsAgree")} variant="light" className="mt-2.5 text-center" testid="ws-agree-note" />
          </section>
        </div>

        {/* RIGHT: report */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          <PrivacyShield />
          <div className="lg:sticky lg:top-[88px] rounded-sm border border-lc-ink/10 bg-white">
            <div className="flex items-center justify-between border-b border-lc-ink/10 px-5 py-3.5">
              <h2 className="font-serif text-lg flex items-center gap-2"><Gauge size={18} weight="duotone" className="text-lc-ocean" /> {t("ws.report")}</h2>
              {result && (
                <div className="flex gap-2">
                  <button data-testid="export-pdf-btn" onClick={() => {
                    if (lang.startsWith("zh")) toast.info(t("ex.preparing"));
                    track("report_exported", { format: "pdf" });
                    exportPDF(result, { id: resultId, model: MODEL }, { labels: exportLabels(t), lang, redacted: !!result._redacted });
                  }}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/10 px-2.5 py-1.5 text-xs font-semibold text-lc-steel hover:bg-lc-paper transition-colors duration-200">
                    <FilePdf size={14} weight="duotone" className="text-lc-critical" /> PDF
                  </button>
                  <button data-testid="export-csv-btn" onClick={() => { track("report_exported", { format: "csv" }); exportCSV(result, { id: resultId }, { labels: exportLabels(t), redacted: !!result._redacted }); }}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/10 px-2.5 py-1.5 text-xs font-semibold text-lc-steel hover:bg-lc-paper transition-colors duration-200">
                    <FileCsv size={14} weight="duotone" className="text-lc-ok" /> CSV
                  </button>
                </div>
              )}
            </div>

            <div className="lc-scroll max-h-[calc(100vh-180px)] overflow-y-auto p-5">
              {/* empty */}
              {!result && !analyzing && (
                <div data-testid="report-empty" className="flex flex-col items-center justify-center py-20 text-center">
                  <Scales size={44} weight="duotone" className="text-lc-ink/20" />
                  <p className="mt-4 font-serif text-lg text-lc-steel">{t("ws.empty")}</p>
                  <p className="mt-1 max-w-xs text-xs text-lc-steel">{t("ws.emptySub")}</p>
                </div>
              )}

              {/* loading */}
              {analyzing && (
                <div data-testid="report-loading" className="py-16 flex flex-col items-center">
                  <CircleNotch size={40} className="animate-spin text-lc-ocean" />
                  <p className="mt-4 font-mono text-xs uppercase tracking-widest text-lc-steel">{t("ws.loadingTitle")}</p>
                  <div className="mt-6 w-full space-y-2">
                    {t("ws.loadSteps").map((label, i) => (
                      <motion.div key={label} initial={{ opacity: 0.2 }} animate={{ opacity: [0.2, 1, 0.2] }}
                        transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.3 }}
                        className="flex items-center gap-2 rounded-sm bg-lc-paper px-3 py-2 text-xs text-lc-steel">
                        <span className="h-1.5 w-1.5 rounded-full bg-lc-ocean" /> {label}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* result */}
              {result && !analyzing && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="report-result">
                  {isSampleData && (
                    <button
                      data-testid="sample-pdf-download-btn"
                      onClick={() => {
                        if (lang.startsWith("zh")) toast.info(t("ex.preparing"));
                        track("sample_report_downloaded", { format: "pdf" });
                        exportPDF(result, { id: resultId || "SAMPLE", model: MODEL, sample: true }, { labels: exportLabels(t), lang, redacted: !!result._redacted });
                      }}
                      className="group mb-4 flex w-full items-center justify-center gap-2 rounded-sm bg-lc-ocean px-4 py-3 text-sm font-semibold text-white hover:bg-lc-navy transition-colors duration-200"
                    >
                      <FilePdf size={17} weight="duotone" /> {t("ws.sampleDownload")}
                      <ArrowRight size={14} weight="bold" className="group-hover:translate-x-0.5 transition-transform duration-200" />
                    </button>
                  )}
                  {result._redacted && (
                    <div data-testid="report-redaction-badge"
                      className="mb-3 inline-flex items-center gap-1.5 rounded-sm border border-lc-ok/30 bg-lc-ok/10 px-2.5 py-1 text-[11px] font-semibold text-lc-ok">
                      <ShieldCheck size={13} weight="fill" /> {t("priv.reportBadge")}
                    </div>
                  )}
                  <div className="flex items-center gap-5">
                    <ScoreRing score={result.readiness_score ?? 0} ready={result.presentation_ready} />
                    <div className="flex-1">
                      <span data-testid="readiness-status" className={`inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-bold ${
                        result.presentation_ready ? "bg-lc-ok/10 text-lc-ok" : "bg-lc-critical/10 text-lc-critical"
                      }`}>
                        {result.presentation_ready ? <SealCheck size={14} weight="fill" /> : <Warning size={14} weight="fill" />}
                        {result.presentation_ready ? t("ws.ready") : t("ws.notReady")}
                      </span>
                      <p className="mt-2 text-xs leading-relaxed text-lc-steel">{result.overview}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {[
                      { k: "critical", n: result.counts?.critical ?? 0 },
                      { k: "warning", n: result.counts?.warning ?? 0 },
                      { k: "info", n: result.counts?.info ?? 0 },
                    ].map(({ k, n }) => {
                      const s = SEV[k];
                      return (
                        <div key={k} data-testid={`count-${k}`} className="rounded-sm border px-3 py-2.5 text-center" style={{ borderColor: s.ring, background: s.bg }}>
                          <div className="font-mono text-2xl font-medium" style={{ color: s.color }}>{n}</div>
                          <div className="font-mono text-[9px] uppercase tracking-wider" style={{ color: s.color }}>{t("ws.sev." + k)}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* discrepancy list */}
                  <div className="mt-5 space-y-3">
                    {(result.discrepancies || [])
                      .slice()
                      .sort((a, b) => (["critical", "warning", "info"].indexOf(a.severity)) - (["critical", "warning", "info"].indexOf(b.severity)))
                      .map((d, i) => {
                        const s = SEV[d.severity] || SEV.info;
                        return (
                          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                            data-testid={`discrepancy-${i}`}
                            className="rounded-sm border bg-white p-3.5" style={{ borderColor: s.ring }}>
                            <div className="flex items-start gap-2.5">
                              <s.Icon size={18} weight="fill" style={{ color: s.color }} className="mt-0.5 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <h4 className="font-semibold text-sm text-lc-ink">{d.title}</h4>
                                  <span className="font-mono text-[10px] break-words rounded-sm px-1.5 py-0.5" style={{ background: s.bg, color: s.color }}>{d.ucp_reference}</span>
                                </div>
                                <p className="mt-1 text-[11px] text-lc-steel">{d.document}</p>
                                <div className="mt-2 grid grid-cols-1 gap-1.5 text-[11px]">
                                  <div className="rounded-sm bg-lc-paper px-2 py-1.5"><span className="font-semibold text-lc-ink">{t("ws.lcRequires")} </span><span className="text-lc-steel">{d.lc_requirement}</span></div>
                                  <div className="rounded-sm bg-lc-paper px-2 py-1.5"><span className="font-semibold text-lc-ink">{t("ws.docShows")} </span><span className="text-lc-steel">{d.document_value}</span></div>
                                </div>
                                <div className="mt-2 rounded-sm border-l-2 px-2 py-1.5 text-[11px]" style={{ borderColor: s.color, background: s.bg }}>
                                  <span className="font-semibold" style={{ color: s.color }}>{t("ws.fix")} </span>
                                  <span className="text-lc-ink">{d.suggested_fix}</span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>

                  {/* amendment drafter */}
                  {(result.counts?.critical ?? 0) > 0 && (
                    <button data-testid="amendment-btn" onClick={draftAmendment}
                      className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-sm border border-lc-ocean/40 bg-lc-ocean/5 px-4 py-3 text-sm font-semibold text-lc-ocean hover:bg-lc-ocean/10 transition-colors duration-200">
                      <EnvelopeOpen size={16} weight="duotone" /> {t("ws.amendment")}
                    </button>
                  )}

                  {/* passed */}
                  {(result.passed_checks || []).length > 0 && (
                    <div className="mt-5" data-testid="checks-passed">
                      <h4 className="font-mono text-[10px] uppercase tracking-widest text-lc-steel">{t("ws.checksPassed")}</h4>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {result.passed_checks.map((p, i) => (
                          <span key={i} data-testid={`check-passed-${i}`}
                            className="flex items-start gap-1.5 rounded-sm border border-lc-ok/15 bg-lc-ok/10 px-2.5 py-1.5 text-[11px] leading-snug text-lc-ok">
                            <CheckCircle size={13} weight="fill" className="mt-[1px] shrink-0" /> <span>{p}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* ---------- STICKY DISCLAIMER ---------- */}
      <footer className="sticky bottom-0 z-30 border-t border-lc-warn/30 bg-lc-warn/10 backdrop-blur-sm">
        <div className="mx-auto max-w-[1500px] px-4 sm:px-6 py-2.5 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-4" data-testid="workspace-disclaimer">
          <div className="flex items-start gap-2.5 flex-1">
            <Warning size={15} weight="fill" className="mt-0.5 shrink-0 text-lc-warn" />
            <p className="text-[11px] leading-tight text-lc-steel">{t("disclaimer.global")}</p>
          </div>
          <FooterLinks variant="light" className="shrink-0 pl-6 sm:pl-0" />
        </div>
      </footer>

      {/* ---------- MODALS / DRAWERS ---------- */}
      <PaywallModal
        open={paywallOpen}
        onClose={() => { setPaywallOpen(false); setPaywallFreeUsed(false); }}
        onSwitchByo={() => setMode("byo")}
        onUnlocked={() => { setEntitled(true); refreshQuota(); }}
        freeUsed={paywallFreeUsed}
        highlightSolo={mode === "byo"}
      />
      <AmendmentModal
        open={amendmentOpen}
        onClose={() => { setAmendmentOpen(false); setAmendmentBrowse(false); }}
        loading={amendmentLoading}
        amendment={amendment}
        browse={amendmentBrowse}
        saved={savedAmendments}
        onDeleteSaved={(id) => setSavedAmendments(deleteAmendment(id))}
      />
      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        items={history}
        onLoad={loadFromHistory}
        onDelete={removeFromHistory}
      />
      <TeamModal open={teamOpen} onClose={() => setTeamOpen(false)} />
      <ApiKeyGuideModal open={keyGuideOpen} onClose={() => setKeyGuideOpen(false)} />
      <HowItWorks open={howOpen} onClose={() => setHowOpen(false)}
        onStartTour={() => { setHowOpen(false); setTimeout(() => setTourOpen(true), 350); }} />
      <InteractiveTour open={tourOpen} onClose={() => setTourOpen(false)} onStepChange={handleTourStep} sampleLoaded={isSampleData} skipSampleStep={new URLSearchParams(window.location.search).get("demo") === "mt700"} />
      <OnboardingWizard
        open={onboardingOpen}
        onClose={(dontShow) => { if (dontShow) setOnboarded(true); setOnboardingOpen(false); }}
        onStart={() => {
          setInputMode("swift");
          setTimeout(() => document.querySelector('[data-testid="swift-input"]')?.focus(), 150);
        }}
      />
    </div>
  );
}
